<template>
  <Transition name="fade-long" appear>
    <div v-if="isPageLoading">
      <pdl-skeleton class="mb-1" width="100%" height="60vh" />
      <div class="py-3 flex flex-col lg:flex-row gap-x-10 relative container">
        <div class="lg:w-3/5">
          <pdl-skeleton class="mb-2" type="heading" width="25%" />
          <pdl-skeleton width="95%" />
          <pdl-skeleton width="90%" />
          <pdl-skeleton width="40%" />
          <pdl-skeleton class="mb-2" height="30px" width="35%" />
        </div>
        <div class="lg:w-1/2 lg:pl-5">
          <pdl-skeleton is-list :list-count="5" />
        </div>
      </div>
      <div class="grid grid-cols-12 mb-6 gap-x-6 px-0 max-w-screen-xxl m-auto md:pt-3 md:min-h-screen lg:pt-4 lg:px-2">
        <div class="col-span-12 lg:col-span-7 xl:col-span-8">
          <div class="sticky top-0">
            <pdl-skeleton class="hidden lg:flex" height="700px" width="100%" type="image" />
            <pdl-skeleton class="flex lg:hidden" height="40vh" width="100%" type="image" />
            <pdl-skeleton :is-list="true" height="100px" :list-count="5" type="square" />
            <div class="flex justify-between">
              <pdl-skeleton height="175px" width="90%" />
              <pdl-skeleton height="175px" width="90%" />
              <pdl-skeleton height="175px" width="90%" />
            </div>
          </div>
        </div>
        <div class="col-span-12 lg:col-span-5 xl:col-span-4">
          <pdl-skeleton
            class="mb-2"
            height="45px"
            width="90%"
            :has-wrapper-margin="false"
            :is-list="true"
            :list-count="5"
          />
          <pdl-skeleton class="mb-2" :is-list="true" height="175px" width="100%" :list-count="3" />
          <pdl-skeleton class="mb-2" height="50px" width="100%" />
          <pdl-skeleton class="mb-2" height="400px" width="100%" />
          <pdl-skeleton class="mb-2" height="400px" width="100%" />
          <pdl-skeleton class="mb-1" :is-list="true" height="50px" width="100%" :list-count="2" />
        </div>
      </div>
      <pdl-skeleton class="mb-2" width="20%" type="heading" />
      <div class="flex">
        <pdl-skeleton class="mr-1 md:mr-3" width="450px" height="750px" />
        <pdl-skeleton class="mr-1 md:mr-3" width="450px" height="750px" />
        <pdl-skeleton width="450px" height="750px" />
      </div>
    </div>
    <div v-else-if="hasErrorLoadingSavedBuild" class="m-2">
      <pdl-callout kind="error">
        <template #content>
          {{ $t('product.fdp.customizable.configUnavailable') }}
        </template>
      </pdl-callout>
    </div>
    <div v-else-if="!hasErrorLoadingFamily && selectedModelId" class="min-h-full min-w-full">
      <h1 v-if="hasFamilyName" class="sr-only">{{ familyDetailsData.familyName }}</h1>
      <div id="family-details-top" qaid="family-details-top-slot" :class="topSlotClasses">
        <headless-slot :slot-position="CMSContentSlotPositions.TOP_SLOT" />
      </div>
      <div
        id="family-details-center-container"
        ref="familyDetailsCenterContainer"
        qaid="family-details-center-container"
        :style="{'--smart-sticky-top': `${stickyTopValue}px`}"
        class="grid grid-cols-1 lg:grid-cols-[1fr_minmax(20em,_30em)] lg:grid-rows-[1fr_auto] gap-4 max-w-screen-xxl mx-auto mb-4 lg:pt-2"
      >
        <div class="fd-column-wrapper relative lg:row-start-1 lg:col-start-1 min-h-xs min-w-xs lg:min-w-md">
          <div
            ref="familyDetailsCenterLeftSlot"
            :class="{sticky: smartColumn === 'left', 'absolute inset-0': isCustomizationMode && !isCompactView}"
            class="top-0"
          >
            <div
              id="sticky-family-customization-image"
              class="sticky top-header-height z-30 bg-white shadow-md mb-2 lg:shadow-none"
              :class="{'lg:hidden': !isCustomizationMode}"
            />
            <div
              id="family-details-center-left-top-slot"
              qaid="family-details-center-left-top-slot"
              class="min-w-xs lg:min-w-md"
              :class="{'lg:min-h-xs': !isCustomizationMode}"
            >
              <headless-slot :slot-position="CMSContentSlotPositions.CENTER_LEFT_TOP_SLOT" />
            </div>
            <div
              v-if="isCompactView"
              id="family-details-center-right-compact-slot"
              qaid="family-details-center-right-compact-slot"
            >
              <headless-slot :slot-position="CMSContentSlotPositions.CENTER_RIGHT_SLOT" />
            </div>
            <div
              v-if="!isCustomizationMode && !isCompactView"
              id="family-details-center-left-bottom-slot"
              qaid="family-details-center-left-bottom-slot"
              class="lg:row-start-2 lg:col-start-1 min-h-xs min-w-xs lg:min-w-md"
            >
              <headless-slot :slot-position="CMSContentSlotPositions.CENTER_LEFT_BOTTOM_SLOT" />
            </div>
          </div>
        </div>
        <div
          v-if="!isCompactView"
          class="fd-column-wrapper relative row-start-1 col-start-2 row-span-2 min-h-xs min-w-xs"
        >
          <div
            id="family-details-center-right-slot"
            ref="familyDetailsCenterRightSlot"
            :class="{sticky: smartColumn === 'right'}"
            qaid="family-details-center-right-slot"
            class="pb-4 top-0"
          >
            <headless-slot :slot-position="CMSContentSlotPositions.CENTER_RIGHT_SLOT" />
          </div>
        </div>
      </div>
      <div
        v-if="isCustomizationMode || isCompactView"
        id="family-details-customization-bottom-slot"
        qaid="family-details-customization-bottom-slot"
        class="min-h-xs min-w-xs lg:min-w-lg"
      >
        <headless-slot :slot-position="CMSContentSlotPositions.CENTER_LEFT_BOTTOM_SLOT" />
      </div>
      <div id="family-details-bottom-slot" qaid="family-details-bottom-slot" class="min-h-xs min-w-xs lg:min-w-lg">
        <headless-slot :slot-position="CMSContentSlotPositions.BOTTOM_SLOT" />
      </div>
    </div>
    <div v-else-if="hasErrorLoadingFamily" class="m-4">
      <pdl-callout kind="error">
        <template #content>
          {{ $t('errorMessage.unableToProcess') }}
        </template>
      </pdl-callout>
    </div>
  </Transition>
</template>

<script setup>
import {computed, watch, useTemplateRef, onUnmounted, ref} from 'vue';
import {useRoute} from 'vue-router';
import {useStore} from '@/store';
import {PdlCallout} from '@pedal/pdl-callout';
import HeadlessSlot from '@/components/containers/headless-page-rendering/HeadlessSlot.vue';
import {PdlSkeleton} from '@pedal/pdl-skeleton';
import {register} from 'swiper/element/bundle';
import {CMSContentSlotPositions} from '@/constants/headless-cms.js';

// Registers swiper for FDP
register();

const store = useStore();
const route = useRoute();

const hasErrorLoadingSavedBuild = computed(() => store.state.bikeCustomization.hasErrorLoadingSavedBuild);
const selectedModelId = computed(() => store.state.familyDetails.selectedModelId);
const hasErrorLoadingFamily = computed(() => store.state.familyDetails.hasErrorLoadingFamily);
const isPageLoading = computed(() => store.state.familyDetails.isPageLoading || store.state.headlessCms.isLoadingCMS);
const isCustomizationMode = computed(() => store.state.productDetails.isCustomizationMode);
const isCompactView = computed(() => store.state.global.isCompactView);
const slotData = computed(() => store.state.headlessCms.slotData);
const familyDetailsData = computed(() => store.state.familyDetails.familyDetailsData);
const hasFamilyName = computed(() => !!familyDetailsData.value?.familyName);

const hasTopSlot = computed(
  () =>
    !!slotData.value?.[CMSContentSlotPositions.TOP_SLOT] &&
    slotData.value?.[CMSContentSlotPositions.TOP_SLOT]?.length > 0
);

const topSlotClasses = computed(() => {
  return {
    'min-h-xs min-w-xs lg:min-w-lg': hasTopSlot.value,
  };
});
const headerHeight = computed(() => store.state.global.headerHeight);

watch(
  () => route.params,
  (newValue, oldValue) => {
    if (newValue?.modelCode !== oldValue?.modelCode) {
      store.commit('familyDetails/setSelectedModelId', newValue?.modelCode);
    }
  }
);

// Sticky two column variables
const scrollContainer = useTemplateRef('familyDetailsCenterContainer');
const rightSlot = useTemplateRef('familyDetailsCenterRightSlot');
const leftSlot = useTemplateRef('familyDetailsCenterLeftSlot');
const smartColumn = ref(null); // 'left', 'right', or null
const stickyTopValue = ref(0);
let headerOffset = headerHeight.value + 16;
let observer = null;
let resizeObserver = null;
let scrollListenerActive = false;
let ticking = null;
let stickyContainerOffset;
let clampMin;
let internalStickyTopValue;
let prevScrollP;
let windowHeight = window.innerHeight;
let smartScrollContainer = null;
let shortHeight;
let isStickyOverflowing = true;
let hasInitialized = false;

// Utility functions
const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const removeSmartScroll = () => {
  if (observer && scrollContainer.value) {
    observer.unobserve(scrollContainer.value);
  }

  if (resizeObserver) {
    resizeObserver.disconnect();
  }

  if (scrollListenerActive) {
    window.removeEventListener('scroll', onScroll);
    window.removeEventListener('resize', handleSmartScrollResize);
  }

  scrollListenerActive = false;

  if (ticking) {
    cancelAnimationFrame(ticking);
    ticking = null;
  }

  smartColumn.value = null;
  stickyTopValue.value = 0;

  hasInitialized = false;
};

onUnmounted(() => {
  removeSmartScroll();
});

const setInitialValues = () => {
  stickyContainerOffset = scrollContainer.value.offsetTop;
  internalStickyTopValue = -stickyContainerOffset;
  stickyTopValue.value = internalStickyTopValue;
  prevScrollP = stickyContainerOffset;
};

const updateCachedValues = () => {
  shortHeight = smartScrollContainer.offsetHeight;
  clampMin = windowHeight - (shortHeight + headerOffset);
  isStickyOverflowing = clampMin < 0;
};

const shouldSetSmartContainer = () => {
  const rightHeight = rightSlot.value?.offsetHeight || 0;
  const leftHeight = leftSlot.value?.offsetHeight || 0;
  const column = rightHeight < leftHeight ? 'right' : 'left';
  const heightDifference = Math.abs(rightHeight - leftHeight);

  // Don't switch if heights are too similar (within 150px)
  if (heightDifference < 120) {
    return {shouldStick: false, column: null};
  }

  return {
    shouldStick: true,
    column: column,
    container: column === 'right' ? rightSlot.value : leftSlot.value,
  };
};

const setSmartContainer = (column, container) => {
  // If not provided, calculate it
  if (!column || !container) {
    const result = shouldSetSmartContainer();
    if (!result.shouldStick) {
      smartColumn.value = null;
      stickyTopValue.value = 0;
      return;
    }
    column = result.column;
    container = result.container;
  }

  smartColumn.value = column;
  smartScrollContainer = container;
};

const setupResizeObserver = () => {
  if (resizeObserver) {
    resizeObserver.disconnect();
  }

  resizeObserver = new ResizeObserver(() => {
    const {shouldStick, column, container} = shouldSetSmartContainer();

    if (!shouldStick) {
      smartColumn.value = null;
      stickyTopValue.value = 0;
      return;
    }

    if (column !== smartColumn.value) {
      setSmartContainer(column, container);
    }

    // Always update cached values when slots resize
    setInitialValues();
    updateCachedValues();
  });

  // Observe both slots
  resizeObserver.observe(leftSlot.value);
  resizeObserver.observe(rightSlot.value);
};

let resizeTimeout;
const handleSmartScrollResize = () => {
  clearTimeout(resizeTimeout);

  resizeTimeout = setTimeout(() => {
    windowHeight = window.innerHeight;
    setSmartContainer();
    setInitialValues();
  }, 150);
};

// Main two column scroll sticky handler
const smartSticky = () => {
  if (!scrollListenerActive) {
    ticking = null;
    return;
  }

  const scrollP = window.scrollY - stickyContainerOffset;

  if (prevScrollP !== scrollP) {
    // Calculate scroll direction and difference
    const direction = scrollP > prevScrollP ? 1 : -1;
    const scrollDif = Math.abs(prevScrollP - scrollP);

    internalStickyTopValue = clamp(internalStickyTopValue - scrollDif * direction, clampMin, 0);
    prevScrollP = scrollP;

    // Update CSS custom property
    stickyTopValue.value = internalStickyTopValue;
  }

  ticking = requestAnimationFrame(smartSticky);
};

// Lightweight scroll listener - keeps ticker alive
let scrollTimeout;
const onScroll = () => {
  // Restart ticker if it was stopped
  if (!ticking && scrollListenerActive && isStickyOverflowing) {
    ticking = requestAnimationFrame(smartSticky);
  }

  // Auto-stop ticker after 150ms of no scrolling
  clearTimeout(scrollTimeout);
  scrollTimeout = setTimeout(() => {
    if (ticking) {
      cancelAnimationFrame(ticking);
      ticking = null;
    }
  }, 150);
};

observer = new IntersectionObserver(
  (entries) => {
    scrollListenerActive = entries[0].isIntersecting;
    if (scrollListenerActive && !ticking) {
      setSmartContainer();
      setupResizeObserver();
      setInitialValues();
      updateCachedValues();
      window.addEventListener('resize', handleSmartScrollResize);
      window.addEventListener('scroll', onScroll, {passive: true});
    } else if (!scrollListenerActive) {
      window.removeEventListener('resize', handleSmartScrollResize);
      window.removeEventListener('scroll', onScroll);
      if (ticking) {
        cancelAnimationFrame(ticking);
        ticking = null;
      }
    }
  },
  {
    root: null,
    rootMargin: '-40% 0px -40% 0px',
  }
);

watch(
  () => isCompactView.value,
  (value) => {
    if (value) {
      removeSmartScroll();
    } else if (scrollContainer.value && leftSlot.value && rightSlot.value) {
      hasInitialized = false;
      observer.observe(scrollContainer.value);
    }
  }
);

watch(
  () => isCustomizationMode.value,
  (value) => {
    if (value) {
      // Entering customization mode - stop sticky behavior
      smartColumn.value = null;
      stickyTopValue.value = 0;
      if (ticking) {
        cancelAnimationFrame(ticking);
        ticking = null;
      }
    } else if (scrollListenerActive && scrollContainer.value && leftSlot.value && rightSlot.value) {
      // Exiting customization mode - restart sticky behavior if in viewport
      setSmartContainer();
      setInitialValues();
      updateCachedValues();
      if (isStickyOverflowing && !ticking) {
        ticking = requestAnimationFrame(smartSticky);
      }
    }
  }
);

const stickyInit = watch(
  [scrollContainer, leftSlot, rightSlot, () => isCompactView.value],
  ([container, left, right, compact]) => {
    if (hasInitialized || compact || !container || !left || !right) {
      return;
    }

    hasInitialized = true;
    observer.observe(container);
    stickyInit(); // Stop watching after first successful initialization
  },
  {immediate: true}
);
</script>
<style lang="scss">
.fade-long-leave-active {
  position: absolute;
  width: 100%;
}

#sticky-family-customization-image {
  &:not(:has(.product-image-container)) {
    visibility: hidden;
  }
}

@media (screen(lg)) {
  .fd-column-wrapper {
    margin-top: calc(-1 * var(--header-height) - 1rem);

    > div {
      padding-top: calc(var(--header-height) + 1rem);
    }

    .sticky:not(#sticky-family-customization-image) {
      top: var(--smart-sticky-top, 0);
    }
  }
}
</style>
